<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import {
  avatarText,
  getAvatarColor,
  useChatStoreCore,
  useNotifications,
} from '../../chatModules'
import AuthenticatedVImg from '../AuthenticatedVImg.vue'
import { ChatApiService } from '@/modules/chat-matrix-fast/services/chatApiService'
import { resolveChatDisplayTitle } from '@/modules/chat-matrix-fast/constants/chat'
import { isTopicsModeChat } from '@/modules/chat-matrix-fast/utils/topics'
import type { IChat, IChatTopic } from '@/modules/chat-matrix-fast/types/IChat'

interface Props {
  modelValue: boolean
  messageCount?: number
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'confirm', payload: {
    destinationChatId: number
    destinationTopicId?: number | null
  }): void
  (e: 'cancel'): void
}

const props = withDefaults(defineProps<Props>(), {
  messageCount: 1,
})
const emit = defineEmits<Emits>()

const coreStore = useChatStoreCore()
const { showError } = useNotifications()

const dialogTitle = computed(() => {
  const n = props.messageCount
  if (n <= 1) {
    return 'Переслать сообщение'
  }
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) {
    return `Переслать ${n} сообщение`
  }
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
    return `Переслать ${n} сообщения`
  }
  return `Переслать ${n} сообщений`
})

const search = ref('')
const selectedChatId = ref<number | null>(null)
const selectedTopicId = ref<number | null>(null)
const expandedChatId = ref<number | null>(null)
const topicsByChatId = ref<Record<number, IChatTopic[]>>({})
const loadingTopicsChatId = ref<number | null>(null)
const isForwarding = ref(false)

const isOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})

const searchQuery = computed(() => search.value.trim().toLowerCase())

const destinationChats = computed(() => {
  const query = searchQuery.value

  return coreStore.chats
    .filter(chat => canReceiveForward(chat))
    .filter(chat => {
      if (!query) return true
      if (resolveChatDisplayTitle(chat).toLowerCase().includes(query)) return true
      return getTopicsForChat(chat).some(topic =>
        topic.title.toLowerCase().includes(query),
      )
    })
    .slice()
    .sort((a, b) => {
      const aTime = Date.parse(a.last_message?.created_at ?? a.created_at ?? '') || 0
      const bTime = Date.parse(b.last_message?.created_at ?? b.created_at ?? '') || 0
      return bTime - aTime
    })
})

const selectedChat = computed(() =>
  coreStore.chats.find(chat => chat.id === selectedChatId.value) ?? null,
)

const needsTopic = computed(() => isTopicsModeChat(selectedChat.value))

const canConfirm = computed(() => {
  if (!selectedChatId.value || isForwarding.value) return false
  if (needsTopic.value && !selectedTopicId.value) return false
  return true
})

function canReceiveForward(chat: IChat): boolean {
  if (chat.capabilities?.can_receive_forward != null) {
    return chat.capabilities.can_receive_forward
  }
  if (chat.capabilities?.can_post != null) {
    return chat.capabilities.can_post
  }
  return chat.type !== 'space'
}

function getTopicsForChat(chat: IChat): IChatTopic[] {
  const cached = topicsByChatId.value[chat.id]
  if (cached) return cached
  return (chat.topics ?? []).filter(topic => !topic.archived)
}

function visibleTopics(chat: IChat): IChatTopic[] {
  const topics = getTopicsForChat(chat)
  const query = searchQuery.value
  if (!query) return topics

  if (resolveChatDisplayTitle(chat).toLowerCase().includes(query)) {
    return topics
  }

  return topics.filter(topic => topic.title.toLowerCase().includes(query))
}

function chatRoleLabel(chat: IChat): string {
  if (chat.type === 'group' || chat.type === 'channel' || chat.type === 'workgroup') {
    return 'Группа'
  }
  if (chat.type === 'event') return 'Задача'
  if (chat.type === 'favorite') return 'Заметки'
  return 'Диалог'
}

watch(
  () => props.modelValue,
  open => {
    if (!open) return
    search.value = ''
    selectedChatId.value = null
    selectedTopicId.value = null
    expandedChatId.value = null
    topicsByChatId.value = {}
    loadingTopicsChatId.value = null
    isForwarding.value = false
  },
)

async function ensureTopicsLoaded(chat: IChat): Promise<IChatTopic[]> {
  const existing = topicsByChatId.value[chat.id]
  if (existing) return existing

  if (chat.topics?.length) {
    const topics = chat.topics.filter(topic => !topic.archived)
    topicsByChatId.value = { ...topicsByChatId.value, [chat.id]: topics }
    return topics
  }

  loadingTopicsChatId.value = chat.id
  try {
    const loaded = await ChatApiService.fetchTopics(chat.id)
    const topics = loaded.filter(topic => !topic.archived)
    topicsByChatId.value = { ...topicsByChatId.value, [chat.id]: topics }
    return topics
  } catch (error) {
    showError(error, 'Load topics for forward')
    return []
  } finally {
    if (loadingTopicsChatId.value === chat.id) {
      loadingTopicsChatId.value = null
    }
  }
}

const selectChat = async (chat: IChat) => {
  if (!isTopicsModeChat(chat)) {
    expandedChatId.value = null
    selectedChatId.value = chat.id
    selectedTopicId.value = null
    return
  }

  expandedChatId.value = chat.id
  selectedChatId.value = chat.id
  selectedTopicId.value = null

  await ensureTopicsLoaded(chat)
  if (expandedChatId.value !== chat.id) return

  const visible = visibleTopics(chat)
  const general = visible.find(topic => topic.general)
  selectedTopicId.value = general?.id ?? visible[0]?.id ?? null
}

const selectTopic = (chat: IChat, topicId: number) => {
  expandedChatId.value = chat.id
  selectedChatId.value = chat.id
  selectedTopicId.value = topicId
}

const isChatActive = (chat: IChat) => {
  if (selectedChatId.value !== chat.id) return false
  return !isTopicsModeChat(chat)
}

const isTopicActive = (chatId: number, topicId: number) =>
  selectedChatId.value === chatId && selectedTopicId.value === topicId

const isExpanded = (chat: IChat) =>
  isTopicsModeChat(chat) && expandedChatId.value === chat.id

const onConfirm = () => {
  if (!canConfirm.value || selectedChatId.value == null) return

  isForwarding.value = true
  emit('confirm', {
    destinationChatId: selectedChatId.value,
    destinationTopicId: needsTopic.value ? selectedTopicId.value : null,
  })
}

const onCancel = () => {
  emit('cancel')
}

const chatTitle = (chat: IChat) => resolveChatDisplayTitle(chat)

const chatAvatar = (chat: IChat) =>
  chat.display_avatar_url || chat.icon || chat.avatar_url || chat.dialog_member?.avatar || ''

defineExpose({
  setForwarding: (value: boolean) => {
    isForwarding.value = value
  },
})
</script>

<template>
  <VDialog
    v-model="isOpen"
    max-width="420"
    persistent
  >
    <VCard class="pa-5">
      <VCardTitle class="text-h6 font-weight-bold mb-2">
        {{ dialogTitle }}
      </VCardTitle>

      <VTextField
        v-model="search"
        density="compact"
        variant="outlined"
        hide-details
        placeholder="Поиск чата или темы"
        prepend-inner-icon="ri-search-line"
        class="mb-3"
      />

      <div class="forward-chat-list">
        <template
          v-for="chat in destinationChats"
          :key="chat.id"
        >
          <button
            type="button"
            class="forward-chat-item"
            :class="{
              'forward-chat-item--active': isChatActive(chat),
              'forward-chat-item--expanded': isExpanded(chat),
            }"
            @click="selectChat(chat)"
          >
            <VAvatar
              size="36"
              :color="chatAvatar(chat) ? undefined : getAvatarColor(chatTitle(chat))"
            >
              <AuthenticatedVImg
                v-if="chatAvatar(chat)"
                :src="chatAvatar(chat)"
              />
              <span v-else>{{ avatarText(chatTitle(chat)) }}</span>
            </VAvatar>
            <div class="forward-chat-meta">
              <div class="forward-chat-title">
                {{ chatTitle(chat) }}
              </div>
              <div class="forward-chat-role text-medium-emphasis">
                {{ chatRoleLabel(chat) }}
              </div>
            </div>
            <VIcon
              v-if="isTopicsModeChat(chat)"
              :icon="isExpanded(chat) ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"
              size="18"
              class="forward-chat-chevron"
            />
          </button>

          <div
            v-if="isExpanded(chat)"
            class="forward-topic-list"
          >
            <div
              v-if="loadingTopicsChatId === chat.id"
              class="forward-topic-loading text-medium-emphasis"
            >
              Загрузка тем…
            </div>

            <button
              v-for="topic in visibleTopics(chat)"
              :key="`${chat.id}-${topic.id}`"
              type="button"
              class="forward-topic-item"
              :class="{ 'forward-topic-item--active': isTopicActive(chat.id, topic.id) }"
              @click="selectTopic(chat, topic.id)"
            >
              <VIcon
                icon="ri-hashtag"
                size="16"
                class="forward-topic-icon"
              />
              <span class="forward-topic-title">{{ topic.title }}</span>
            </button>

            <div
              v-if="loadingTopicsChatId !== chat.id && !visibleTopics(chat).length"
              class="forward-topic-loading text-medium-emphasis"
            >
              Нет доступных тем
            </div>
          </div>
        </template>

        <div
          v-if="!destinationChats.length"
          class="text-body-2 text-medium-emphasis text-center py-6"
        >
          Нет доступных чатов
        </div>
      </div>

      <VCardActions class="mt-4 justify-end gap-3 pa-0">
        <VBtn
          variant="outlined"
          color="grey"
          :disabled="isForwarding"
          @click="onCancel"
        >
          Отмена
        </VBtn>
        <VBtn
          variant="elevated"
          color="primary"
          :loading="isForwarding"
          :disabled="!canConfirm"
          @click="onConfirm"
        >
          Переслать
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped>
.v-card {
  border-radius: 12px;
}

.v-btn {
  text-transform: none;
  font-weight: 500;
}

.forward-chat-list {
  max-height: 360px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.forward-chat-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  color: inherit;
}

.forward-chat-item:hover {
  background: rgba(var(--v-theme-on-surface), 0.05);
}

.forward-chat-item--active,
.forward-chat-item--expanded {
  background: rgba(var(--v-theme-primary), 0.08);
}

.forward-chat-meta {
  min-width: 0;
  flex: 1;
}

.forward-chat-title {
  font-size: 0.9375rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.forward-chat-role {
  font-size: 0.75rem;
}

.forward-chat-chevron {
  color: rgba(var(--v-theme-on-surface), 0.45);
  flex-shrink: 0;
}

.forward-topic-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 2px 0 6px;
}

.forward-topic-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-left: 28px;
  padding: 7px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  color: inherit;
}

.forward-topic-item:hover {
  background: rgba(var(--v-theme-on-surface), 0.05);
}

.forward-topic-item--active {
  background: rgba(var(--v-theme-primary), 0.14);
}

.forward-topic-icon {
  color: rgba(var(--v-theme-on-surface), 0.45);
  flex-shrink: 0;
}

.forward-topic-title {
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.forward-topic-loading {
  margin-left: 28px;
  padding: 6px 10px;
  font-size: 0.8125rem;
}
</style>
